import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { toast } from 'react-toastify'
import api from '../../services/api'
import {
  GET_TRUCKS,
  GET_TRUCK,
  POST_ADD_TRUCK,
  PUT_UPDATE_TRUCK,
  DELETE_TRUCK
} from '../../helpers/url_helper'

// Async thunk for fetching all trucks
export const fetchTrucks = createAsyncThunk(
  'truck/fetchAll',
  async (params = {}, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams()
      if (params.page) queryParams.append('page', params.page)
      if (params.limit) queryParams.append('limit', params.limit)
      if (params.isActive !== undefined) queryParams.append('isActive', params.isActive)
      
      const url = queryParams.toString() ? `${GET_TRUCKS}?${queryParams.toString()}` : GET_TRUCKS
      const response = await api.get(url)
      return response.data.trucks
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to fetch trucks'
      if (error.response?.status !== 401) toast.error(message)
      return rejectWithValue(message)
    }
  }
)

// Async thunk for fetching single truck
export const fetchTruck = createAsyncThunk(
  'truck/fetchOne',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`${GET_TRUCK}/${id}`)
      return response.data.truck
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to fetch truck details'
      if (error.response?.status !== 401) toast.error(message)
      return rejectWithValue(message)
    }
  }
)

// Async thunk for adding truck
export const addTruck = createAsyncThunk(
  'truck/add',
  async (truckData, { rejectWithValue }) => {
    try {
      const response = await api.post(POST_ADD_TRUCK, truckData)
      toast.success('Truck added successfully')
      return response.data.truck
    } catch (error) {
      const message = error.response?.data?.error || 
                     error.response?.data?.message || 
                     'Failed to add truck'
      toast.error(message)
      return rejectWithValue(message)
    }
  }
)

// Async thunk for updating truck
export const updateTruck = createAsyncThunk(
  'truck/update',
  async ({ id, truckData }, { rejectWithValue }) => {
    try {
      const response = await api.put(`${PUT_UPDATE_TRUCK}/${id}`, truckData)
      toast.success('Truck updated successfully')
      return response.data.truck
    } catch (error) {
      const message = error.response?.data?.error || 
                     error.response?.data?.message || 
                     'Failed to update truck'
      toast.error(message)
      return rejectWithValue(message)
    }
  }
)

// Async thunk for deleting truck
export const deleteTruck = createAsyncThunk(
  'truck/delete',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`${DELETE_TRUCK}/${id}`)
      toast.success('Truck deleted successfully')
      return id
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to delete truck'
      toast.error(message)
      return rejectWithValue(message)
    }
  }
)

const initialState = {
  trucks: [],
  selectedTruck: null,
  loading: false,
  error: null,
}

const truckSlice = createSlice({
  name: 'truck',
  initialState,
  reducers: {
    clearError: state => {
      state.error = null
    },
    clearSelectedTruck: state => {
      state.selectedTruck = null
    },
  },
  extraReducers: builder => {
    builder
      // Fetch All Trucks
      .addCase(fetchTrucks.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchTrucks.fulfilled, (state, action) => {
        state.loading = false
        state.trucks = action.payload
      })
      .addCase(fetchTrucks.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      
      // Fetch Single Truck
      .addCase(fetchTruck.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchTruck.fulfilled, (state, action) => {
        state.loading = false
        state.selectedTruck = action.payload
      })
      .addCase(fetchTruck.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      
      // Add Truck
      .addCase(addTruck.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(addTruck.fulfilled, (state, action) => {
        state.loading = false
        state.trucks.push(action.payload)
      })
      .addCase(addTruck.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      
      // Update Truck
      .addCase(updateTruck.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(updateTruck.fulfilled, (state, action) => {
        state.loading = false
        const index = state.trucks.findIndex(truck => truck.id === action.payload.id)
        if (index !== -1) {
          state.trucks[index] = action.payload
        }
      })
      .addCase(updateTruck.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      
      // Delete Truck
      .addCase(deleteTruck.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(deleteTruck.fulfilled, (state, action) => {
        state.loading = false
        state.trucks = state.trucks.filter(truck => truck.id !== action.payload)
      })
      .addCase(deleteTruck.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
  },
})

export const { clearError, clearSelectedTruck } = truckSlice.actions
export default truckSlice.reducer
